/**
 * Prepare a picked photo for upload.
 *
 * Two things go wrong with files straight out of a phone's picker:
 *
 *   1. Format. Android hands the WebView whatever the camera saved — often
 *      HEIF/HEIC when "high efficiency" is on — while iOS transcodes to JPEG
 *      before the file ever reaches the page. That asymmetry is why picking a
 *      photo worked on an iPhone and was rejected on Android. Neither Chrome
 *      nor the server's sharp build can be relied on to decode HEIC, so we
 *      fall back to a real HEIF decoder, lazily loaded only for those files.
 *   2. Size. A 12MP photo is regularly 6-8MB, over the server's 5MB image cap.
 *
 * Both are fixed by decoding the file in the browser and re-encoding it. If
 * nothing here can decode the format, the ORIGINAL file is returned untouched
 * so the server still gets its own chance at it and can report precisely.
 *
 * A file that is already a web format and already small enough is returned
 * as-is — no needless re-encode, no quality loss.
 */

const WEB_SAFE = ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp"];

const HEIF_BRANDS = ["heic", "heix", "heim", "heis", "hevc", "hevx", "hevm", "hevs", "mif1", "msf1"];

/**
 * Is this file a HEIF/HEIC container? The declared type is unreliable on
 * Android, so the first bytes decide: an ISO base-media box whose major brand
 * is one of the HEIF image brands.
 */
async function isHeif(file: File): Promise<boolean> {
  const type = (file.type || "").toLowerCase();
  if (type.includes("heic") || type.includes("heif")) return true;
  try {
    const head = new Uint8Array(await file.slice(0, 12).arrayBuffer());
    if (head.length < 12) return false;
    const ascii = (from: number, to: number) =>
      String.fromCharCode(...Array.from(head.subarray(from, to)));
    return ascii(4, 8) === "ftyp" && HEIF_BRANDS.includes(ascii(8, 12));
  } catch {
    return false;
  }
}

/** Decode to a bitmap, preferring the fast path but tolerating old engines. */
async function decode(file: Blob): Promise<ImageBitmap | HTMLImageElement | null> {
  if (typeof createImageBitmap === "function") {
    try {
      // `from-image` applies the EXIF orientation, so portrait photos don't
      // come out sideways.
      return await createImageBitmap(file, { imageOrientation: "from-image" } as any);
    } catch {
      /* fall through to the <img> path */
    }
  }
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null);
    };
    img.src = url;
  });
}

/**
 * Last resort for HEIC: a JS HEIF decoder. It is ~1.3MB, so it is imported
 * dynamically — it only ever reaches a device that actually picked a HEIC.
 */
async function heifToJpeg(file: File): Promise<Blob | null> {
  try {
    const mod: any = await import("heic2any");
    const convert = mod.default || mod;
    const out = await convert({ blob: file, toType: "image/jpeg", quality: 0.9 });
    return Array.isArray(out) ? out[0] : (out as Blob);
  } catch {
    return null;
  }
}

export async function normalizeImageForUpload(
  file: File,
  opts: { maxDim?: number; maxBytes?: number; quality?: number } = {}
): Promise<File> {
  const maxDim = opts.maxDim ?? 1600;
  const maxBytes = opts.maxBytes ?? 4.5 * 1024 * 1024;
  const quality = opts.quality ?? 0.85;

  const type = (file.type || "").toLowerCase();
  if (WEB_SAFE.includes(type) && file.size <= maxBytes) return file;

  // An animated GIF would lose its animation on a canvas round-trip, and its
  // frames are usually small anyway — leave it alone.
  if (type === "image/gif") return file;

  let source = await decode(file);

  // Nothing in the browser could read it. If it is a HEIC, decode it properly
  // and try again on the JPEG that comes back.
  if (!source && (await isHeif(file))) {
    const jpeg = await heifToJpeg(file);
    if (jpeg) source = await decode(jpeg);
  }
  if (!source) return file;

  const srcW = "width" in source ? source.width : 0;
  const srcH = "height" in source ? source.height : 0;
  if (!srcW || !srcH) return file;

  const scale = Math.min(1, maxDim / Math.max(srcW, srcH));
  const width = Math.max(1, Math.round(srcW * scale));
  const height = Math.max(1, Math.round(srcH * scale));

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) return file;
  ctx.drawImage(source as CanvasImageSource, 0, 0, width, height);
  if ("close" in source && typeof source.close === "function") source.close();

  // Keep PNG as PNG so transparency survives (logos, badges); everything else
  // becomes JPEG, which is what actually shrinks a photo.
  const outType = type === "image/png" ? "image/png" : "image/jpeg";
  const blob: Blob | null = await new Promise((resolve) =>
    canvas.toBlob(resolve, outType, outType === "image/jpeg" ? quality : undefined)
  );
  if (!blob) return file;

  const base = file.name.replace(/\.[^.]+$/, "") || "image";
  const ext = outType === "image/png" ? ".png" : ".jpg";
  return new File([blob], `${base}${ext}`, { type: outType, lastModified: Date.now() });
}
